import {InterventionStatus} from "@/medication-checkup/interfaces/interventions";

/**
 * How the thread is painted, where several scenes paint the same thing.
 *
 * Only what more than one card wears lives here: the sheet they are drawn
 * on, the chip they label things with, and the pill the pharmacist picks an
 * answer by. Each card keeps its own geometry — how wide it runs, how it
 * rounds, whether it clips what it holds — since that is where they
 * legitimately differ.
 */

/**
 * The sheet a card of the thread is drawn on: the white surface, its border
 * and its shadow, indented under the avatar of the Copilote so every card
 * hangs off the line that announced it.
 */
export const CARD_SHEET = 'ml-10 bg-white border border-slate-200 shadow-custom';

/**
 * The chip a card labels a status, a level or a source by. A chip carrying an
 * icon adds its own gap.
 */
export const CHIP = 'inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold';

/**
 * The ring around a question the pharmacist must still answer, drawn inside
 * so a card that clips what it holds keeps it.
 */
export const FLAGGED = 'ring-1 ring-inset ring-red-light rounded-lg';

/**
 * The pill an answer is picked by — a yes, a no, a treatment named: filled in
 * while it stands, outlined while it does not.
 */
export const pillClasses = (selected: boolean): string => {
    return selected
        ? 'bg-primary text-white border-primary'
        : 'bg-white text-gray-dark border-gray';
};

/**
 * The tone a status is labelled by, shared by the table that proposes a
 * change and the one that records what the doctor decided of it.
 */
export const STATUS_CHIPS: Record<InterventionStatus, string> = {
    unchanged: 'bg-gray-light text-muted-foreground',
    posology_modified: 'bg-orange/30 text-gray-dark',
    replaced: 'bg-lightPurple-lightest text-primary',
    stopped: 'bg-red-light/15 text-red-light',
    added: 'bg-secondary-lightest text-secondary-darkest'
};
